import {
  addDays,
  addMonths,
  eachWeekOfInterval,
  endOfMonth,
  set,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { range } from "lodash-es";

import { getLocalTimeZone } from "@app/util/dates";

import { WEEK_OPTIONS } from "../../consts";
import UserLocationDatePeriod from "../../enums/UserLocationDatePeriod";
import PlaybackTimelineTickUnit from "../enums/PlaybackTimelineTickUnit";
import PlaybackTimeline, { PlaybackTimelineTick } from "../types/PlaybackTimeline";

// Hours between Day-view ticks: 8a 10a 12p 2p 4p 6p.
const DAY_TICK_STEP_HOURS = 2;

// The standard working day. A Day window covers these org-tz hours, and grows past them only when a
// rep has activity outside them.
const DAY_STANDARD_START_HOUR = 8;
const DAY_STANDARD_END_HOUR = 18;

const HOURS_PER_DAY = 24;

// Week view is Mon..Sun — seven columns, matching the efficiency chart's axis.
const DAYS_IN_WEEK = 7;

// Reads `date`'s wall-clock fields as an epoch time in `timeZone`. The inverse of parseApiDateWithTz,
// and the only place this module crosses from calendar space into real epoch times.
const toTimeMs = (date: Date, timeZone: string): number => fromZonedTime(date, timeZone).getTime();

const toTicks = (
  timesMs: readonly number[],
  startMs: number,
  endMs: number
): PlaybackTimelineTick[] =>
  timesMs.map((timeMs) => ({
    progress: (timeMs - startMs) / (endMs - startMs),
    timeMs,
  }));

// Every even clock hour of the org day that falls inside the window, bounds inclusive — so a day
// whose first activity is exactly 08:00 still shows its 8a tick. A Day window lies within a single
// org-tz calendar day (events are bucketed by createdAtIsoDate), so enumerating that day's even
// hours covers it.
//
// Each candidate is built by SETTING the hour field on the window's calendar day — never by stepping
// a toZonedTime value with addHours. addHours is addMilliseconds under the hood, i.e. raw epoch
// arithmetic, while the hour is read back through the runtime's own getters: a DST boundary in the
// VIEWER's zone (not the org's) inside the window makes a "+2h" step land 1 or 3 hours away, putting
// odd-hour ticks on the rail for any org, twice a year, for that viewer. Field-based construction
// reduces that coupling rather than removing it: no odd-hour tick is ever emitted, but a tick can be
// dropped when the candidate hour does not exist in the viewer's zone.
const buildEvenHourTimesMs = (startMs: number, endMs: number, timeZone: string): number[] => {
  const windowStartWallClock = toZonedTime(startMs, timeZone);

  const timesMs: number[] = [];
  for (let hour = 0; hour < HOURS_PER_DAY; hour += DAY_TICK_STEP_HOURS) {
    const candidate = set(windowStartWallClock, {
      hours: hour,
      milliseconds: 0,
      minutes: 0,
      seconds: 0,
    });
    const timeMs = toTimeMs(candidate, timeZone);
    if (timeMs < startMs || timeMs > endMs) {
      continue;
    }
    // Spring-forward: this hour may not exist in the org's zone that day, and fromZonedTime resolves
    // the gap to an instant in an adjacent hour (02:00 in New_York lands on 01:00, not 03:00) — which
    // would put an odd-hour tick on the rail. Drop it: 2am genuinely did not happen, so that day
    // legitimately shows one tick fewer.
    if (toZonedTime(timeMs, timeZone).getHours() !== hour) {
      continue;
    }
    timesMs.push(timeMs);
  }
  return timesMs;
};

// The calendar structure of the rail for one period. Day spans the standard working day of the events'
// own calendar day and so never consults selectedDate; Week and Month are pure calendar arithmetic and
// ignore the events entirely. Returns undefined only when no window can be expressed — whether there
// is anything worth animating is canPlay's separate concern.
//
// IMPORTANT: only selectedDate's wall-clock fields may be read (date-fns calendar ops + toTimeMs).
// It arrives as an org-tz "lying" Date from the saga and the efficiency chart, but as a browser-local
// Date from the date picker; the two agree on y/m/d and disagree on getTime(), so any arithmetic on
// its epoch value would break for users whose browser timezone differs from the org's.
const buildPlaybackTimeline = (
  period: UserLocationDatePeriod,
  selectedDate: Date | undefined,
  orgTimeZone: string | undefined,
  sortedEventTimesMs: readonly number[]
): PlaybackTimeline | undefined => {
  const timeZone = orgTimeZone ?? getLocalTimeZone();

  if (period === UserLocationDatePeriod.Day) {
    if (sortedEventTimesMs.length < 2) {
      return undefined;
    }
    const firstEventMs = sortedEventTimesMs[0];
    const lastEventMs = sortedEventTimesMs[sortedEventTimesMs.length - 1];
    if (lastEventMs <= firstEventMs) {
      return undefined;
    }
    // The standard hours are anchored on the events' own org-tz calendar day, not on selectedDate. The
    // two name the same day, but the events are the data the map draws, while selectedDate already
    // holds the new day for the renders between a date change and its data.
    const eventDayWallClock = toZonedTime(firstEventMs, timeZone);
    const toStandardHourMs = (hours: number) =>
      toTimeMs(
        set(eventDayWallClock, { hours, milliseconds: 0, minutes: 0, seconds: 0 }),
        timeZone
      );
    const startMs = Math.min(toStandardHourMs(DAY_STANDARD_START_HOUR), firstEventMs);
    const endMs = Math.max(toStandardHourMs(DAY_STANDARD_END_HOUR), lastEventMs);
    return {
      endMs,
      startMs,
      ticks: toTicks(buildEvenHourTimesMs(startMs, endMs, timeZone), startMs, endMs),
      tickUnit: PlaybackTimelineTickUnit.Hour,
    };
  }

  if (!selectedDate) {
    return undefined;
  }

  if (period === UserLocationDatePeriod.Week) {
    const weekStart = startOfWeek(selectedDate, WEEK_OPTIONS);
    const startMs = toTimeMs(weekStart, timeZone);
    const endMs = toTimeMs(addDays(weekStart, DAYS_IN_WEEK), timeZone);
    if (endMs <= startMs) {
      return undefined;
    }
    const dayStarts = range(DAYS_IN_WEEK).map((dayIndex) =>
      toTimeMs(addDays(weekStart, dayIndex), timeZone)
    );
    return {
      endMs,
      startMs,
      ticks: toTicks(dayStarts, startMs, endMs),
      tickUnit: PlaybackTimelineTickUnit.Weekday,
    };
  }

  const monthStart = startOfMonth(selectedDate);
  const startMs = toTimeMs(monthStart, timeZone);
  const endMs = toTimeMs(addMonths(monthStart, 1), timeZone);
  if (endMs <= startMs) {
    return undefined;
  }
  // Same bucketing as the efficiency chart's Month branch, so the rail's W1..Wn and the chart's bars
  // always describe the same weeks. eachWeekOfInterval's first entry can precede the month, hence the
  // clamp that pins W1 to the 1st.
  const weekStarts = eachWeekOfInterval(
    { end: endOfMonth(selectedDate), start: monthStart },
    WEEK_OPTIONS
  ).map((weekStart) => toTimeMs(weekStart < monthStart ? monthStart : weekStart, timeZone));
  return {
    endMs,
    startMs,
    ticks: toTicks(weekStarts, startMs, endMs),
    tickUnit: PlaybackTimelineTickUnit.Week,
  };
};

export default buildPlaybackTimeline;
